import { OIDCMethodOptions } from "../clients/slashid-api"
import { PersonHandle } from "../clients/slashid-api"
import { Factor, UserTokenText } from "../domain/interfaces"
import { ValidationRule } from "../domain/passwords"
import * as time from "./time"
import { BaseUser } from "../main/user.base"
import { ClientGeneratedErrorSlug } from "../domain/errors.internal"
import { EventHandler, EventMeta } from "../domain/emitter"
import { UICustomization } from "../domain/interfaces"

export async function waitForFocus(): Promise<boolean> {
    const hadFocus = document.hasFocus()

    // Polling document.hasFocus() is not ideal but its result reliably changes
    // when the window gets focus, unlike the "focus" and "focusin" events, which
    // only get triggered when an input field gets focus.
    while (!hadFocus && !document.hasFocus()) {
        await time.sleep(50)
    }

    return hadFocus
}

/**
 * This event is emitted once the OTP code is sent via SMS.
 *
 * @deprecated please use OtpCodeSentEvent instead.
 */
export type OtpSmsSentEvent = void

/**
 * This event is emitted once the OTP code is sent.
 */
export type OtpCodeSentEvent = void

/**
 * This event is emitted once the SDK is ready to register new TOTP credential.
 */
export type TotpKeyGenerated = {
    uri: string
    qrCode: string
    recoveryCodes: string[]
}

/**
 * This event is emitted once the SDK is ready to verify a TOTP code.
 */
export type TotpCodeRequested = void

export const OAuthFailureReason = {
    PopupBlocked: "Popup blocked",
    BadOAuthURL: "Bad OAuth sign in URL",
} as const

export const PopupFailureReason = {
    PopupBlocked: "Popup blocked",
    BadAuthURL: "Bad authentication URL",
} as const

export const IdFailureReason = {
    ApiCallFailed: "API call failed",
} as const

/**
 * This event is emitted once the SDK is ready to accept a new password. It means the handle has been verified.
 */
export type PasswordSetReadyEvent = void

/**
 * This event is emitted once the SDK is ready to verify an existing password.
 */
export type PasswordVerifyReadyEvent = void

/**
 * This event is emitted once the SDK is ready to accept a new password as part of the password recovery flow.
 */
export type PasswordResetReadyEvent = void

/**
 * This event needs to be emitted when the end user enters their password.
 */
export type PasswordSubmittedEvent = string

/**
 * This events can be emitted while the flow is processing the challenges to cancel it.
 */
export type FlowCancelled = void

/**
 * This event is emitted once the OAuth flow in the popup UX mode was started
 */
export type OAuthFlowStartedEvent = Omit<OIDCMethodOptions, "method">

/**
 * This event is emitted when the OAuthFlow fails with the specified reason
 */
export type OAuthFlowFailedEvent = Omit<OIDCMethodOptions, "method"> & {
    reason: (typeof OAuthFailureReason)[keyof typeof OAuthFailureReason]
}

/**
 * This event is emitted if the submitted OTP code is incorrect.
 */
export type OtpIncorrectCodeSubmittedEvent = {
    factor: Factor
    challengeId: string
}

/**
 * This event should be emitted when the end user enters the OTP code they received.
 * Event payload is a string containing the OTP code.
 */
export type OtpCodeSubmittedEvent = string

/**
 * This event is emitted when the submitted password does not meet the validation rules.
 */
export type InvalidPasswordSubmittedEvent = FlowErrorMetadata & { failedRules: ValidationRule[] }

/**
 * This event is emitted when the submitted password does not match the password stored for the given handle.
 */
export type IncorrectPasswordSubmittedEvent = FlowErrorMetadata

export type UserAuthenticatedFromURLEvent = { token: string }

export type PersonIdentifiedEvent = {
    user: BaseUser
}

export type PersonLoggedOutEvent = {
    token: UserTokenText
}

/**
 * This event is emitted when the .id method resolves successfully.
 */
export type IdFlowSucceededEvent = {
    token: UserTokenText
    handle?: PersonHandle
    authenticationFactor?: Factor
}

export type IdFlowStartedEvent = {
    authenticationFactor?: Factor
}

export type RecoveryFlowStartedEvent = {
    handle: PersonHandle
    authenticationFactor: Factor
}

export type RecoveryFlowSucceededEvent = {
    handle: PersonHandle
    authenticationFactor: Factor
}

export type FlowErrorMetadata = {
    authenticationFactor?: Factor
    handle?: PersonHandle
    previousToken?: string
    challengeId?: string
    failureDetail?: string
}

export type IdFlowFailedEvent = FlowErrorMetadata & {
    errorText: ClientGeneratedErrorSlug
}

export type RecoveryFlowFailedEvent = FlowErrorMetadata & {
    errorText: ClientGeneratedErrorSlug
}

/**
 * An unexpected client side error occurred within the SDK, potentially interrupting authentication.
 */
export type ClientSideErrorEvent = FlowErrorMetadata & {
    errorText: "client_side_error"
}

export function isClientSideErrorEvent(event: any): event is ClientSideErrorEvent {
    return event.errorText === "client_side_error"
}

export type WebAuthnChallengeProcessed = {
    credentialId: string
}

export type RedirectUriDiscoveredEvent = {
    redirectUri: string
}

export type UICustomizationReceivedEvent = {
    customizationProperties: UICustomization
}

export type PublicReadEvents = {
    otpCodeSent: OtpCodeSentEvent
    otpSmsSent: OtpSmsSentEvent // Deprecated
    totpKeyGenerated: TotpKeyGenerated
    totpCodeRequested: TotpCodeRequested
    otpIncorrectCodeSubmitted: OtpIncorrectCodeSubmittedEvent
    passwordSetReady: PasswordSetReadyEvent
    passwordVerifyReady: PasswordVerifyReadyEvent
    passwordResetReady: PasswordResetReadyEvent
    invalidPasswordSubmitted: InvalidPasswordSubmittedEvent
    incorrectPasswordSubmitted: IncorrectPasswordSubmittedEvent
    oauthFlowStarted: OAuthFlowStartedEvent
    oauthFlowFailed: OAuthFlowFailedEvent
    userAuthenticatedFromURL: UserAuthenticatedFromURLEvent
    idFlowSucceeded: IdFlowSucceededEvent
    idFlowStarted: IdFlowStartedEvent
    idFlowFailed: IdFlowFailedEvent
    recoveryFlowStarted: RecoveryFlowStartedEvent
    recoveryFlowSucceeded: RecoveryFlowSucceededEvent
    recoveryFlowFailed: RecoveryFlowFailedEvent
    webAuthnChallengeProcessed: WebAuthnChallengeProcessed
    redirectUriDiscovered: RedirectUriDiscoveredEvent
    uiCustomizationReceived: UICustomizationReceivedEvent
}

export type PublicWriteEvents = {
    otpCodeSubmitted: OtpCodeSubmittedEvent
    passwordSubmitted: PasswordSubmittedEvent
    flowCancelled: FlowCancelled
}

export type PrivateWriteEvents = {
    personIdentified: PersonIdentifiedEvent
    personLoggedOut: PersonLoggedOutEvent
}

/**
 * We don't differentiate between read and write here
 * because there is no clear API boundary for private
 * events.
 *
 * This event can be fired by any internal slashid source, and
 * may be listened to by any internal slashid source. There is
 * no concept of "them" and "us" here.
 *
 * The source is never a third party.
 */
export type PrivateEvents = {
    clientSideError: ClientSideErrorEvent
}

type PublicReadEventsKeys = Array<keyof PublicReadEvents>
const PUBLIC_READ_EVENTS: PublicReadEventsKeys = [
    "otpSmsSent", // Deprecated
    "otpCodeSent",
    "totpKeyGenerated",
    "totpCodeRequested",
    "otpIncorrectCodeSubmitted",
    "passwordSetReady",
    "passwordResetReady",
    "passwordVerifyReady",
    "invalidPasswordSubmitted",
    "incorrectPasswordSubmitted",
    "oauthFlowStarted",
    "oauthFlowFailed",
    "userAuthenticatedFromURL",
    "idFlowSucceeded",
    "idFlowStarted",
    "idFlowFailed",
    "recoveryFlowStarted",
    "recoveryFlowSucceeded",
    "recoveryFlowFailed",
    "webAuthnChallengeProcessed",
    "redirectUriDiscovered",
    "uiCustomizationReceived",
]

type PublicWriteEventsKeys = Array<keyof PublicWriteEvents>
const PUBLIC_WRITE_EVENTS: PublicWriteEventsKeys = ["otpCodeSubmitted", "passwordSubmitted", "flowCancelled"]

type PrivateEventsKeys = Array<keyof PrivateEvents>
const PRIVATE_EVENTS: PrivateEventsKeys = ["clientSideError"]

export function isPublicReadEvent(evt: any): evt is keyof PublicReadEvents {
    return PUBLIC_READ_EVENTS.includes(evt)
}

export function isPublicWriteEvent(evt: any): evt is keyof PublicWriteEvents {
    return PUBLIC_WRITE_EVENTS.includes(evt)
}

export function isPrivateEvent(evt: any): evt is keyof PrivateEvents {
    return PRIVATE_EVENTS.includes(evt)
}

export type Events = PublicReadEvents & PublicWriteEvents

// internal events

/**
 * This event is emitted when the Popup UX mode fails with the specified reason
 */
type PopupFailedEvent = {
    reason: (typeof PopupFailureReason)[keyof typeof PopupFailureReason]
}

type InternalEvents = {
    popupFailed: PopupFailedEvent
}

export type Handler<T = unknown> = (event: T) => void

export type NamesToEventsMap = Events & InternalEvents & PrivateEvents

export type EventsWithNamesMap = {
    [K in keyof NamesToEventsMap]: { name: K; payload: NamesToEventsMap[K]; meta?: EventMeta }
}

export type NamedEvents = EventsWithNamesMap[keyof EventsWithNamesMap]

/**
 * Keeps track of the legacy handlers and their wrapped counterparts.
 * This allows us to remove them when the legacy handler is removed.
 */
export function createLegacyHandlerDecoratorFactory<
    EventName extends keyof NamesToEventsMap
>(): LegacyHandlerDecoratorFactory<EventName> {
    const legacyToWrappedHandlerMap = new Map<Handler<NamesToEventsMap[EventName]>, EventHandler<NamedEvents>>()

    function decorate(legacyHandler: Handler<NamesToEventsMap[EventName]>): EventHandler<NamedEvents> {
        function decoratedHandler(event: NamedEvents) {
            // TODO type check fails because of event payload types that are void or string
            // @ts-expect-error
            legacyHandler(event.payload)
        }

        legacyToWrappedHandlerMap.set(legacyHandler, decoratedHandler)

        return decoratedHandler
    }

    function getDecorator(legacyHandler: Handler<NamesToEventsMap[EventName]>): undefined | EventHandler<NamedEvents> {
        if (legacyToWrappedHandlerMap.has(legacyHandler)) {
            return legacyToWrappedHandlerMap.get(legacyHandler)
        }

        return undefined
    }

    return {
        decorate,
        getDecorator,
    }
}

export interface LegacyHandlerDecoratorFactory<EventName extends keyof NamesToEventsMap> {
    decorate: (legacyHandler: Handler<NamesToEventsMap[EventName]>) => EventHandler<NamedEvents>
    getDecorator: (legacyHandler: Handler<NamesToEventsMap[EventName]>) => undefined | EventHandler<NamedEvents>
}
